import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Trophy,
  Star,
  Target,
  Zap,
  Crown,
  Award,
  Sparkles,
  TrendingUp,
  Users,
  CheckCircle,
  Gift,
  Rocket
} from "lucide-react";
import { useTrialManagement } from "@/hooks/useTrialManagement";
import { motion, AnimatePresence } from "framer-motion";

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: 'onboarding' | 'engagement' | 'mastery' | 'collaboration';
  points: number;
  isUnlocked: boolean;
  progress: number;
  maxProgress: number;
  reward?: {
    type: 'badge' | 'discount' | 'feature_unlock' | 'trial_extension';
    value: string;
  };
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
}

export const AchievementSystem: React.FC = () => {
  const { milestones, trackEngagement } = useTrialManagement();
  const [showCelebration, setShowCelebration] = useState<string | null>(null);
  const [totalPoints, setTotalPoints] = useState(0);

  const [achievements, setAchievements] = useState<Achievement[]>([
    {
      id: 'first_upload',
      title: 'First Steps',
      description: 'Upload your first bill and experience AI extraction',
      icon: Rocket,
      category: 'onboarding',
      points: 100,
      isUnlocked: false,
      progress: 0,
      maxProgress: 1,
      tier: 'bronze',
      reward: { type: 'badge', value: 'Early Adopter' }
    },
    {
      id: 'power_user',
      title: 'Power User',
      description: 'Process 10 bills with high confidence scores',
      icon: Zap,
      category: 'engagement',
      points: 250,
      isUnlocked: false,
      progress: 0,
      maxProgress: 10,
      tier: 'silver',
      reward: { type: 'feature_unlock', value: 'Advanced Analytics' }
    },
    {
      id: 'team_builder',
      title: 'Team Builder',
      description: 'Invite and onboard 3 team members',
      icon: Users,
      category: 'collaboration',
      points: 200,
      isUnlocked: false,
      progress: 0,
      maxProgress: 3,
      tier: 'silver',
      reward: { type: 'trial_extension', value: '7 days' }
    },
    {
      id: 'efficiency_master',
      title: 'Efficiency Master',
      description: 'Achieve 95%+ accuracy on 20 bills',
      icon: Target,
      category: 'mastery',
      points: 500,
      isUnlocked: false,
      progress: 0,
      maxProgress: 20,
      tier: 'gold',
      reward: { type: 'discount', value: '20% off first year' }
    },
    {
      id: 'integration_expert',
      title: 'Integration Expert',
      description: 'Connect 3 different integrations',
      icon: Crown,
      category: 'mastery',
      points: 300,
      isUnlocked: false,
      progress: 0,
      maxProgress: 3,
      tier: 'gold',
      reward: { type: 'feature_unlock', value: 'API Access' }
    },
    {
      id: 'champion',
      title: 'Trial Champion',
      description: 'Complete all onboarding milestones',
      icon: Trophy,
      category: 'mastery',
      points: 1000,
      isUnlocked: false,
      progress: 0,
      maxProgress: 1,
      tier: 'platinum',
      reward: { type: 'discount', value: '30% off first year + Priority Support' }
    }
  ]);

  // Simulate achievement progress based on milestones and engagement
  useEffect(() => {
    if (milestones) {
      const completedMilestones = milestones.filter(m => m.is_completed).length;
      
      setAchievements(prev => prev.map(achievement => {
        let newProgress = achievement.progress;
        let isUnlocked = achievement.isUnlocked;

        // Simulate progress based on milestone completion and category
        switch (achievement.category) {
          case 'onboarding':
            newProgress = Math.min(completedMilestones, achievement.maxProgress);
            break;
          case 'engagement':
            newProgress = Math.min(completedMilestones * 2, achievement.maxProgress);
            break;
          case 'collaboration':
            newProgress = Math.min(Math.floor(completedMilestones / 2), achievement.maxProgress);
            break;
          case 'mastery':
            newProgress = Math.min(Math.floor(completedMilestones * 1.5), achievement.maxProgress);
            break;
        }

        // Check if achievement should be unlocked
        if (!isUnlocked && newProgress >= achievement.maxProgress) {
          isUnlocked = true;
          setShowCelebration(achievement.id);
          
          // Track achievement unlock
          trackEngagement.mutate({
            feature_used: `achievement_${achievement.id}`,
            session_duration_minutes: 1,
            actions_performed: 1,
            value_realized: achievement.points / 1000
          });
        }

        return { ...achievement, progress: newProgress, isUnlocked };
      }));
    }
  }, [milestones]);

  // Calculate total points
  useEffect(() => {
    const points = achievements
      .filter(a => a.isUnlocked)
      .reduce((sum, a) => sum + a.points, 0);
    setTotalPoints(points);
  }, [achievements]);

  const getTierColor = (tier: Achievement['tier']) => {
    switch (tier) {
      case 'bronze': return 'text-orange-600 border-orange-200 bg-orange-50 dark:bg-orange-950/30';
      case 'silver': return 'text-slate-600 border-slate-200 bg-slate-50 dark:bg-slate-950/30';
      case 'gold': return 'text-yellow-600 border-yellow-200 bg-yellow-50 dark:bg-yellow-950/30';
      case 'platinum': return 'text-purple-600 border-purple-200 bg-purple-50 dark:bg-purple-950/30';
      default: return 'text-gray-600 border-gray-200 bg-gray-50 dark:bg-gray-950/30';
    }
  };

  const getTierBadgeColor = (tier: Achievement['tier']) => {
    switch (tier) {
      case 'bronze': return 'bg-orange-500';
      case 'silver': return 'bg-slate-500';
      case 'gold': return 'bg-yellow-500';
      case 'platinum': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const categorizedAchievements = {
    onboarding: achievements.filter(a => a.category === 'onboarding'),
    engagement: achievements.filter(a => a.category === 'engagement'),
    mastery: achievements.filter(a => a.category === 'mastery'),
    collaboration: achievements.filter(a => a.category === 'collaboration')
  };

  return (
    <div className="space-y-6">
      {/* Achievement Summary */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            Achievement Center
          </CardTitle>
          <CardDescription>
            Unlock rewards and track your trial progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{totalPoints}</div>
              <div className="text-sm text-muted-foreground">Total Points</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {achievements.filter(a => a.isUnlocked).length}
              </div>
              <div className="text-sm text-muted-foreground">Achievements Unlocked</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {Math.round((achievements.filter(a => a.isUnlocked).length / achievements.length) * 100)}%
              </div>
              <div className="text-sm text-muted-foreground">Completion Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Achievement Categories */}
      {Object.entries(categorizedAchievements).map(([category, categoryAchievements]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="capitalize text-lg">
              {category} Achievements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categoryAchievements.map((achievement) => {
                const Icon = achievement.icon;
                const progressPercentage = (achievement.progress / achievement.maxProgress) * 100;

                return (
                  <motion.div
                    key={achievement.id}
                    className={`relative p-4 rounded-lg border transition-all ${
                      achievement.isUnlocked 
                        ? getTierColor(achievement.tier)
                        : 'border-muted bg-muted/30'
                    }`}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        achievement.isUnlocked 
                          ? getTierBadgeColor(achievement.tier)
                          : 'bg-muted'
                      } text-white`}>
                        {achievement.isUnlocked ? (
                          <CheckCircle className="h-6 w-6" />
                        ) : (
                          <Icon className="h-6 w-6" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-foreground">
                            {achievement.title}
                          </h3>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="secondary" 
                              className={`${getTierBadgeColor(achievement.tier)} text-white text-xs`}
                            >
                              {achievement.tier.toUpperCase()}
                            </Badge>
                            <span className="text-sm font-medium text-primary">
                              {achievement.points}pts
                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mb-3">
                          {achievement.description}
                        </p>

                        <div className="space-y-2">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Progress</span>
                            <span>{achievement.progress}/{achievement.maxProgress}</span>
                          </div>
                          <Progress value={progressPercentage} className="h-2" />
                        </div>

                        {achievement.reward && (
                          <div className="mt-3 p-2 rounded bg-accent/50 border border-accent">
                            <div className="flex items-center gap-1 text-xs">
                              <Gift className="h-3 w-3 text-primary" />
                              <span className="font-medium">Reward:</span>
                              <span className="text-muted-foreground">
                                {achievement.reward.value}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {achievement.isUnlocked && (
                      <div className="absolute top-2 right-2">
                        <Sparkles className="h-5 w-5 text-primary animate-pulse" />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Celebration Modal */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowCelebration(null)}
          >
            <motion.div
              className="bg-card p-8 rounded-lg border shadow-lg text-center max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Achievement Unlocked! 🎉</h3>
              <p className="text-muted-foreground mb-4">
                You've earned a new achievement and points!
              </p>
              <Button onClick={() => setShowCelebration(null)}>
                Awesome!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};